import type { AxiosError } from 'axios';
import type { ReportPeriod, ReportsSummaryResponse } from '@eriko/shared-types';
import { apiClient } from '../lib/api';

export interface ReportsQueryParams {
  orgId: string;
  period: ReportPeriod;
  from?: string;
  to?: string;
}

function buildSearch(params: ReportsQueryParams): URLSearchParams {
  const search = new URLSearchParams({ orgId: params.orgId, period: params.period });
  if (params.period === 'custom' && params.from && params.to) {
    search.set('from', params.from);
    search.set('to', params.to);
  }
  return search;
}

export async function getReportsSummary(
  params: ReportsQueryParams,
): Promise<ReportsSummaryResponse> {
  const res = await apiClient.get<ReportsSummaryResponse>(
    `/reports/summary?${buildSearch(params).toString()}`,
  );
  return res.data;
}

function filenameFromDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename="?([^"]+)"?/i);
  return match?.[1] ?? fallback;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

/**
 * When a download fails, axios returns the response body as a Blob (because we
 * requested responseType: 'blob'). The admin apiClient's envelope-unwrap
 * short-circuits on Blobs, so we re-parse here and surface the backend message.
 */
async function rethrowBlobError(err: unknown): Promise<never> {
  const axiosErr = err as AxiosError | undefined;
  const blob = axiosErr?.response?.data;
  if (blob instanceof Blob && blob.type.includes('json')) {
    try {
      const text = await blob.text();
      const body = JSON.parse(text) as {
        success?: boolean;
        error?: { code?: string; message?: string };
      };
      if (body && body.success === false && body.error) {
        const e = new Error(body.error.message || 'Download failed') as Error & {
          code?: string;
          status?: number;
        };
        e.code = body.error.code;
        e.status = axiosErr?.response?.status;
        throw e;
      }
    } catch (parseErr) {
      if (parseErr instanceof Error && (parseErr as Error & { code?: string }).code) {
        throw parseErr;
      }
    }
  }
  throw err;
}

export async function downloadReportPdf(params: ReportsQueryParams): Promise<string> {
  try {
    const res = await apiClient.get<Blob>(
      `/reports/pdf?${buildSearch(params).toString()}`,
      { responseType: 'blob' },
    );
    const filename = filenameFromDisposition(
      res.headers?.['content-disposition'] ?? res.headers?.['Content-Disposition'],
      'eriko-report.pdf',
    );
    triggerBlobDownload(res.data, filename);
    return filename;
  } catch (err) {
    await rethrowBlobError(err);
    throw err;
  }
}

export async function downloadReportCsv(params: ReportsQueryParams): Promise<string> {
  try {
    const res = await apiClient.get<Blob>(
      `/reports/csv?${buildSearch(params).toString()}`,
      { responseType: 'blob' },
    );
    const filename = filenameFromDisposition(
      res.headers?.['content-disposition'] ?? res.headers?.['Content-Disposition'],
      'eriko-report.csv',
    );
    triggerBlobDownload(res.data, filename);
    return filename;
  } catch (err) {
    await rethrowBlobError(err);
    throw err;
  }
}
